
import { jwtDecode } from "jwt-decode";

export const getProfile = async () => {
  const storedToken = localStorage.getItem("token") || sessionStorage.getItem("token");
  if (storedToken) {
    try {
      const response = await fetch("/api/users/me", {
        headers: {
          Authorization: `Bearer ${storedToken}`,
        },
      });
      if (!response.ok) {
        const errorBody = await response.text();
        console.error(`Failed to fetch profile: Status ${response.status}, Body: ${errorBody}`);
        throw new Error(`Failed to fetch profile: Status ${response.status}`);
      }
      return await response.json();
    } catch (e) {
      console.error("Error fetching profile:", e);
    }
  }
  return null;
};

export const editProfile = async (profileData: {
  name?: string;
  nickName?: string;
  email?: string;
  twitchId?: string;
  country?: string;
  birthday?: string;
  accountVisibility?: 'public' | 'private' | 'friends';
  phoneNumber?: string;
  receiveNews?: boolean;
}) => {
  const storedToken = localStorage.getItem("token") || sessionStorage.getItem("token");
  console.log('API: Stored Token:', storedToken ? 'Present' : 'Not Present');
  if (storedToken) {
    try {
      const requestBody = JSON.stringify(profileData);
      console.log('API: Request Body for PATCH /api/users/me:', requestBody);
      const response = await fetch('/api/users/me', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${storedToken}`,
        },
        body: requestBody,
      });
      console.log('API: Raw Response from PATCH /api/users/me:', response);
      return response.json();
    } catch (e) {
      console.error("API: Error in editProfile:", e);
    }
  }
};

export const resetPassword = async (password: string) => {
  const storedToken = localStorage.getItem("token") || sessionStorage.getItem("token");
  if (storedToken) {
    try {
      const response = await fetch('/api/users/reset-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${storedToken}`,
        },
        body: JSON.stringify({ password }),
      });
      return response.json();
    } catch (e) {
      console.error("Invalid token:", e);
    }
  }
};

export const getUserAssets = async () => {
  const storedToken = localStorage.getItem("token") || sessionStorage.getItem("token");
  if (!storedToken) {
    console.error("No token found.");
    return [];
  }

  try {
    // Fetch current user's profile to get their ID
    const userProfileResponse = await fetch("/api/users/me", {
      headers: {
        Authorization: `Bearer ${storedToken}`,
      },
    });
    if (!userProfileResponse.ok) {
      throw new Error('Failed to fetch user profile');
    }
    const userProfile = await userProfileResponse.json();
    const currentUserId = userProfile.id; // Assuming the user profile has an 'id' field

    // Fetch all user_assets entries
    const allUserAssetsResponse = await fetch("/api/all/user_assets", {
      headers: {
        Authorization: `Bearer ${storedToken}`,
      },
    });
    if (!allUserAssetsResponse.ok) {
      throw new Error('Failed to fetch all user assets from /api/all/user_assets');
    }
    const allUserAssets = await allUserAssetsResponse.json();

    // Filter user_assets by current user's ID
    const currentUserAssetEntries = allUserAssets.filter((ua: any) => ua.user_id === currentUserId);

    // Fetch details for each asset_id
    const userAssetsWithDetails = await Promise.all(currentUserAssetEntries.map(async (ua: any) => {
      const assetDetailsResponse = await fetch(`/api/all/assets/${ua.asset_id}`, {
        headers: {
          Authorization: `Bearer ${storedToken}`,
        },
      });
      if (!assetDetailsResponse.ok) {
        console.error(`Failed to fetch asset details for asset_id: ${ua.asset_id}`);
        return null; // Return null for failed fetches
      }
      const assetDetails = await assetDetailsResponse.json();

      // Fetch all listings to check if this asset is listed
      const allListingsResponse = await fetch("/api/all/listings", {
        headers: {
          Authorization: `Bearer ${storedToken}`,
        },
      });
      if (!allListingsResponse.ok) {
        console.error("Failed to fetch all listings");
        // Continue without listing info if fetch fails
      }
      const allListings = allListingsResponse.ok ? await allListingsResponse.json() : [];
      const isListed = allListings.some((listing: any) => listing.asset_id === ua.asset_id && listing.user_id === currentUserId);

      return {
        id: ua.id, // ID from the user_assets table entry
        asset: {
          name: assetDetails.name,
          description: assetDetails.description,
          image: assetDetails.image_path, // Use image_path from backend
        },
        quantity: ua.quantity,
        isListed: isListed, // Add isListed property
      };
    }));

    // Filter out any nulls from failed asset detail fetches
    return userAssetsWithDetails.filter(Boolean);
  } catch (e) {
    console.error("Error fetching and filtering user assets:", e);
    return [];
  }
};

export const getUserItems = async () => {
  // Placeholder for fetching user items
  console.log("Fetching user items...");
  return [];
};

export const updateWallet = async (walletAddress: string) => {
  const storedToken = localStorage.getItem("token") || sessionStorage.getItem("token");
  if (storedToken) {
    try {
      const response = await fetch('/api/users/wallet', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${storedToken}`,
        },
        body: JSON.stringify({ walletAddress }),
      });
      return response.json();
    } catch (e) {
      console.error("Invalid token:", e);
    }
  }
};

export const deleteUserAccount = async (password: string) => {
  const storedToken = localStorage.getItem("token") || sessionStorage.getItem("token");
  if (!storedToken) {
    console.error("No token found.");
    return { success: false, message: "No authentication token found." };
  }

  try {
    const response = await fetch('/api/users/me', {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${storedToken}`,
      },
      body: JSON.stringify({ password }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      return { success: false, message: errorData.message || "Failed to delete account." };
    }

    return { success: true, message: "Account deletion initiated." };
  } catch (e) {
    console.error("Error deleting account:", e);
    return { success: false, message: "An error occurred during account deletion." };
  }
};
